import React, { useState } from 'react';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    Title,
    CategoryScale,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';

ChartJS.register(LineElement, PointElement, LinearScale, Title, CategoryScale, Tooltip, Legend);

interface AccessorialReviewTrendProps {
    accessorialsPerformance: any;
}

type ViewMode = 'daily' | 'weekly' | 'monthly';

const AccessorialReviewTrend: React.FC<AccessorialReviewTrendProps> = ({ accessorialsPerformance }) => {
    const [viewMode, setViewMode] = useState<ViewMode>('daily');

    const groupData = (data: any[], mode: ViewMode) => {
        const grouped: Record<string, any> = {};

        data.forEach((item) => {
            let key = '';
            const date = moment(item.dateAdded);

            if (mode === 'daily') {
                key = date.format('YYYY-MM-DD');
            } else if (mode === 'weekly') {
                key = `Week ${date.isoWeek()} - ${date.year()}`;
            } else if (mode === 'monthly') {
                key = date.format('MMM YYYY');
            }

            if (!grouped[key]) {
                grouped[key] = {
                    operationsPending: 0,
                    operationsAccepted: 0,
                    operationsRejected: 0,
                    billingAccepted: 0,
                    billingRejected: 0,
                    acceptedAmount: 0,
                };
            }

            grouped[key].operationsPending += item.operationsPending;
            grouped[key].operationsAccepted += item.operationsAccepted;
            grouped[key].operationsRejected += item.operationsRejected;
            grouped[key].billingAccepted += item.billingAccepted;
            grouped[key].billingRejected += item.billingRejected;
            grouped[key].acceptedAmount += item.acceptedAmount;
        });

        return grouped;
    };

    const getChartData = (accessorialsPerformance: any[]) => {
        if (!accessorialsPerformance || !accessorialsPerformance.length) {
            return { labels: [], datasets: [] };
        }

        const grouped = groupData(accessorialsPerformance, viewMode);
        const labels = Object.keys(grouped);

        const totalFindings = labels.map(
            (label) =>
                grouped[label].operationsPending +
                grouped[label].operationsAccepted +
                grouped[label].operationsRejected
        );
        const operationsAccepted = labels.map((label) => grouped[label].operationsAccepted);
        const operationsRejected = labels.map((label) => grouped[label].operationsRejected);
        const billingAccepted = labels.map((label) => grouped[label].billingAccepted);
        const billingRejected = labels.map((label) => grouped[label].billingRejected);
        const acceptedAmount = labels.map((label) => grouped[label].acceptedAmount);

        return {
            labels,
            datasets: [
                {
                    label: 'Total Findings',
                    data: totalFindings,
                    borderColor: '#4285F4',
                    backgroundColor: 'rgba(66, 133, 244, 0.2)',
                    tension: 0.4,
                },
                {
                    label: 'Operations Accepted',
                    data: operationsAccepted,
                    borderColor: '#0F9D58',
                    backgroundColor: 'rgba(15, 157, 88, 0.2)',
                    tension: 0.4,
                },
                {
                    label: 'Operations Rejected',
                    data: operationsRejected,
                    borderColor: '#FBBC05',
                    backgroundColor: 'rgba(251, 188, 5, 0.2)',
                    tension: 0.4,
                },
                {
                    label: 'Billing Accepted',
                    data: billingAccepted,
                    borderColor: '#A142F4',
                    backgroundColor: 'rgba(161, 66, 244, 0.2)',
                    tension: 0.4,
                },
                {
                    label: 'Billing Rejected',
                    data: billingRejected,
                    borderColor: '#EA4335',
                    backgroundColor: 'rgba(234, 67, 53, 0.2)',
                    tension: 0.4,
                },
                {
                    label: 'Realized Revenue',
                    data: acceptedAmount,
                    borderColor: '#00ACC1',
                    backgroundColor: 'rgba(0, 172, 193, 0.2)',
                    tension: 0.4,
                },
            ],
        };
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text:
                    viewMode === 'daily'
                        ? 'Daily Accessorial Review Trend'
                        : viewMode === 'weekly'
                            ? 'Weekly Accessorial Review Trend'
                            : 'Monthly Accessorial Review Trend',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
            },
        },
    };

    return (
        <div style={{ width: '90%', margin: '0 auto' }}>
            <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <Button
                    theme={ButtonThemes.blue}
                    size={ButtonSizes.autoMedium}
                    onClick={() => setViewMode('daily')}
                    disabled={viewMode === 'daily'}
                    style={{ marginLeft: 8 }}
                >Daily
                </Button>
                <Button
                    theme={ButtonThemes.blue}
                    size={ButtonSizes.autoMedium}
                    onClick={() => setViewMode('weekly')}
                    disabled={viewMode === 'weekly'}
                    style={{ marginLeft: 8 }}
                >Weekly
                </Button>
                <Button
                    theme={ButtonThemes.blue}
                    size={ButtonSizes.autoMedium}
                    onClick={() => setViewMode('monthly')}
                    disabled={viewMode === 'monthly'}
                    style={{ marginLeft: 8 }}
                >Monthly
                </Button>
                {/* <button >

                </button>
                <button style={{ marginLeft: 8 }}>
                    Monthly
                </button> */}
            </div>
            <Line data={getChartData(accessorialsPerformance)} options={options} />
        </div>
    );
};

export default AccessorialReviewTrend;